import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import apiService from '../services/api';

/**
 * 공지 팝업 생성/편집.
 * :id 있으면 편집, 없으면 신규.
 */
export default function AnnouncementForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const editId = id ? parseInt(id, 10) : null;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState(0);
  const [showFrom, setShowFrom] = useState(''); // yyyy-MM-ddTHH:mm
  const [showUntil, setShowUntil] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [image, setImage] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // datetime-local 은 YYYY-MM-DDTHH:mm 형식 요구
  const toLocalInput = (iso: string): string => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  useEffect(() => {
    if (!isEdit || editId === null) {
      // 신규: 기본값으로 지금~1주일
      const now = new Date();
      const until = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      setShowFrom(toLocalInput(now.toISOString()));
      setShowUntil(toLocalInput(until.toISOString()));
      return;
    }
    setLoading(true);
    apiService
      .getAnnouncement(editId)
      .then((a) => {
        setTitle(a.title);
        setContent(a.content);
        setPriority(a.priority);
        setShowFrom(toLocalInput(a.showFrom));
        setShowUntil(toLocalInput(a.showUntil));
        setIsActive(a.isActive);
        setExistingImageUrl(a.imageUrl ?? null);
      })
      .catch((e) => setError(e?.response?.data?.message ?? '조회 실패'))
      .finally(() => setLoading(false));
  }, [isEdit, editId]);

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      setError('제목·본문은 필수입니다');
      return;
    }
    if (!showFrom || !showUntil) {
      setError('노출 기간을 입력해주세요');
      return;
    }
    if (new Date(showUntil) < new Date(showFrom)) {
      setError('종료일이 시작일보다 빠릅니다');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // datetime-local 은 로컬 시각 문자열. ISO 로 변환해 서버 전송
      const showFromIso = new Date(showFrom).toISOString();
      const showUntilIso = new Date(showUntil).toISOString();
      if (isEdit && editId !== null) {
        await apiService.updateAnnouncement(editId, {
          title,
          content,
          priority,
          showFrom: showFromIso,
          showUntil: showUntilIso,
          isActive,
          image,
          removeImage: removeImage && !image,
        });
      } else {
        await apiService.createAnnouncement({
          title,
          content,
          priority,
          showFrom: showFromIso,
          showUntil: showUntilIso,
          isActive,
          image,
        });
      }
      navigate('/announcements');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/announcements')}
        >
          목록
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 2 }}>
          <CampaignIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">
            {isEdit ? '공지 편집' : '새 공지 등록'}
          </Typography>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <TextField
            label="제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="본문"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            fullWidth
            multiline
            rows={5}
            helperText="줄바꿈은 앱 팝업에서 그대로 표시됩니다"
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="노출 시작"
              type="datetime-local"
              value={showFrom}
              onChange={(e) => setShowFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="노출 종료"
              type="datetime-local"
              value={showUntil}
              onChange={(e) => setShowUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          </Stack>

          <TextField
            label="우선순위"
            type="number"
            value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value || '0', 10))}
            helperText="숫자가 클수록 먼저 노출. 동일 시각에 여러 공지가 활성일 때 정렬 기준"
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              이미지 (선택)
            </Typography>
            {existingImageUrl && !removeImage && !image && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <img
                  src={existingImageUrl}
                  alt=""
                  style={{ maxHeight: 120, borderRadius: 4 }}
                />
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setRemoveImage(true)}
                >
                  기존 이미지 제거
                </Button>
              </Stack>
            )}
            {image && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <img
                  src={URL.createObjectURL(image)}
                  alt=""
                  style={{ maxHeight: 120, borderRadius: 4 }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => setImage(null)}
                >
                  선택 해제
                </Button>
              </Stack>
            )}
            <Button variant="outlined" component="label">
              이미지 파일 선택
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setImage(f);
                    setRemoveImage(false);
                  }
                }}
              />
            </Button>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="활성 (체크 해제 시 노출 기간 안이어도 앱에 뜨지 않음)"
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => navigate('/announcements')} disabled={saving}>
              취소
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? '저장 중...' : isEdit ? '변경 저장' : '등록'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
